import * as React from 'react';

/** Main header height (48px) minus 1px so the group header overlaps its bottom border. */
const STICKY_TOP_OFFSET_PX = 47;

interface StickyGroupHeaderState {
  /** Index of the group whose header is currently pinned below the table header. */
  activeIndex: number;
  /** Negative offset applied while the next group's header pushes the pinned one up. */
  pushOffset: number;
}

/**
 * Drives the "one group header pinned at a time" behaviour of grouped tables: on scroll, finds
 * the last group header at or above the sticky line and how far the following header pushes it.
 * Group header rows register themselves through the returned ref callback. Re-measures when the
 * number of groups changes so a freshly added group is taken into account.
 */
export function useStickyGroupHeader(groupCount: number) {
  const headerRefs = React.useRef<(HTMLTableRowElement | null)[]>([]);
  const [state, setState] = React.useState<StickyGroupHeaderState>({
    activeIndex: 0,
    pushOffset: 0,
  });

  const registerHeaderRef = React.useCallback(
    (index: number) => (element: HTMLTableRowElement | null) => {
      headerRefs.current[index] = element;
    },
    []
  );

  React.useEffect(() => {
    if (groupCount === 0) return;
    const container = findScrollContainer(headerRefs.current[0]);
    if (!container) return;

    let rafId: number | null = null;
    const update = () => {
      const headers = headerRefs.current.filter(Boolean) as HTMLTableRowElement[];
      if (headers.length > 0) setState(computeStickyState(headers, container));
    };
    const onScroll = () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(update);
    };

    container.addEventListener('scroll', onScroll, { passive: true });
    update();

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      container.removeEventListener('scroll', onScroll);
    };
  }, [groupCount]);

  return { registerHeaderRef, ...state };
}

function findScrollContainer(from: HTMLElement | null): Element | null {
  if (!from) return null;
  const marked = from.closest('[data-scroll-container]');
  if (marked) return marked;

  let parent = from.parentElement;
  while (parent) {
    const { overflow } = window.getComputedStyle(parent);
    if (overflow === 'auto' || overflow === 'scroll') return parent;
    parent = parent.parentElement;
  }
  return null;
}

function computeStickyState(
  headers: HTMLTableRowElement[],
  container: Element
): StickyGroupHeaderState {
  const stickyTop = container.getBoundingClientRect().top + STICKY_TOP_OFFSET_PX;

  for (let index = headers.length - 1; index >= 0; index--) {
    const headerRect = headers[index].getBoundingClientRect();
    if (headerRect.top > stickyTop) continue;

    const nextHeader = headers[index + 1];
    const gap = nextHeader
      ? nextHeader.getBoundingClientRect().top - stickyTop - headerRect.height
      : 0;
    return { activeIndex: index, pushOffset: Math.min(gap, 0) };
  }

  return { activeIndex: 0, pushOffset: 0 };
}
